import { Injectable, inject, signal, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { Observable, tap, catchError, of, map } from 'rxjs';
import { DocumentationPath, DocumentationPathTreeNode } from '../models/documentation-path.model';

@Injectable({
  providedIn: 'root',
})
export class DocumentationPathService {
  private http = inject(HttpClient);
  private apiUrl = `${environment.apiUrl}/api/ticketcenter/documentation-paths`;

  // State (signals)
  readonly treeNodes = signal<DocumentationPathTreeNode[]>([]);
  readonly selectedPath = signal<DocumentationPath | null>(null);
  readonly loading = signal(false);
  readonly searchQuery = signal('');

  // Derived state (computed)
  readonly breadcrumb = computed(() => {
    const path = this.selectedPath();
    if (!path) return [];
    return path.fullPath.split(' / ').map(name => ({ label: name }));
  });

  readonly hasSelection = computed(() => this.selectedPath() !== null);

  // API calls
  loadTree(depth: number = 2): Observable<DocumentationPath[]> {
    this.loading.set(true);
    return this.http.get<{ data: DocumentationPath[] }>(`${this.apiUrl}/tree?depth=${depth}`).pipe(
      map(response => response.data),
      tap(nodes => {
        this.treeNodes.set(nodes.map(n => this.mapToTreeNode(n)));
        this.loading.set(false);
      }),
      catchError(err => {
        this.loading.set(false);
        throw err;
      })
    );
  }

  loadChildren(parentId: string): Observable<DocumentationPath[]> {
    return this.http
      .get<{ data: DocumentationPath[] }>(`${this.apiUrl}/tree?parent_id=${parentId}&depth=1`)
      .pipe(map(response => response.data));
  }

  getPath(id: string): Observable<DocumentationPath> {
    return this.http
      .get<{ data: DocumentationPath }>(`${this.apiUrl}/${id}`)
      .pipe(map(response => response.data));
  }

  createPath(data: Partial<DocumentationPath>): Observable<DocumentationPath> {
    const payload = this.cleanPayload(data);
    return this.http
      .post<{ data: DocumentationPath }>(this.apiUrl, payload)
      .pipe(map(response => response.data));
  }

  updatePath(id: string, data: Partial<DocumentationPath>): Observable<DocumentationPath> {
    const payload = this.cleanPayload(data);
    return this.http
      .patch<{ data: DocumentationPath }>(`${this.apiUrl}/${id}`, payload)
      .pipe(map(response => response.data));
  }

  private cleanPayload(data: Partial<DocumentationPath>): any {
    const payload: any = { ...data };
    if ('parentId' in payload) {
      payload.parent_id = payload.parentId;
      delete payload.parentId;
    }
    return payload;
  }

  deletePath(id: string): Observable<any> {
    return this.http.delete(`${this.apiUrl}/${id}`);
  }

  movePath(id: string, newParentId: string | null, position?: number): Observable<any> {
    const payload: any = { new_parent_id: newParentId, position };
    return this.http.post(`${this.apiUrl}/${id}/move`, payload);
  }

  reorderPaths(
    parentId: string | null,
    items: { id: string; position: number }[]
  ): Observable<any> {
    const payload = { parent_id: parentId, items };
    return this.http.post(`${this.apiUrl}/reorder`, payload);
  }

  searchPaths(query: string): Observable<DocumentationPath[]> {
    this.loading.set(true);
    return this.http.get<{ data: DocumentationPath[] }>(`${this.apiUrl}/tree?q=${query}`).pipe(
      map(response => response.data),
      tap(nodes => {
        this.treeNodes.set(nodes.map(n => this.mapToTreeNode(n)));
        this.loading.set(false);
      }),
      catchError(err => {
        this.loading.set(false);
        return of([]);
      })
    );
  }

  // Helpers
  mapToTreeNode(path: any): DocumentationPathTreeNode {
    // A API pode retornar snake_case, precisamos garantir que as propriedades corretas sejam acessadas
    const hasChildren = path.hasChildren !== undefined ? path.hasChildren : path.has_children;
    const pathChildren = path.children || [];

    const mappedChildren =
      pathChildren.length > 0 ? pathChildren.map((c: any) => this.mapToTreeNode(c)) : undefined;

    // Mapeia full_path para fullPath no frontend
    const mappedData = { ...path } as any;
    if (path.full_path) {
      mappedData.fullPath = path.full_path;
    }

    return {
      key: path.id,
      label: path.name,
      data: mappedData as DocumentationPath,
      icon: path.icon || 'pi pi-folder',
      leaf: !hasChildren,
      expanded: false, // Inicialmente fechado
      // No PrimeNG, se 'leaf' é false (é uma pasta) mas children não foi carregado da API,
      // children deve ser omitido (undefined) para mostrar o ícone de expansão.
      // Contudo, se a API retornou um array (mesmo que vazio), passamos esse array.
      children: hasChildren && !path.children ? undefined : mappedChildren || [],
      draggable: true,
      droppable: true, // A checagem de regras em si acontece no onDrop via depth
    };
  }
}
